"use client"

import { useState, useEffect, useCallback, useRef } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import Image from "@/src/components/Image"
import toast, { Toaster } from "react-hot-toast"
import { lineChannelsAPI, linkedAccountsAPI, LineChannel } from "@/src/lib/api/line-channels"
import { lineGroupsAPI, LineGroupMapping } from "@/src/lib/api/line-groups"
import { branchesAPI, Branch } from "@/src/lib/api/branches"
import { messagingAccountsAPI, MessagingAccount } from "@/src/lib/api/messaging-accounts"
import { getBusinessId } from "@/lib/api/axios-instance"
import { Loader2, Copy, CheckCircle2, Trash2, ExternalLink, Link2, RefreshCw, GitBranch } from "lucide-react"

type ConnectionType = "line-oa" | "line-chat" | "line-group"
type TokenRegenerationStatus = "idle" | "changed" | "unchanged" | "error"

interface ConnectionFormProps {
  selectedType: ConnectionType
  onClose: () => void
  onChannelsChange?: () => void
  isLineChatConnected?: boolean
}

export function ConnectionForm({ selectedType, onClose: _onClose, onChannelsChange, isLineChatConnected = false }: ConnectionFormProps) {
  const [activeTab, setActiveTab] = useState<ConnectionType>(selectedType)

  // LINE OA form state
  const [channelId, setChannelId] = useState("")
  const [channelSecret, setChannelSecret] = useState("")
  const [channelAccessToken, setChannelAccessToken] = useState("")
  const [channelName, setChannelName] = useState("")
  const [selectedOABranchId, setSelectedOABranchId] = useState<string>("")

  // API state
  const [channels, setChannels] = useState<LineChannel[]>([])
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [disconnecting, setDisconnecting] = useState<string | null>(null)
  const [disconnectingChat, setDisconnectingChat] = useState<string | null>(null)
  const [copiedWebhook, setCopiedWebhook] = useState(false)

  // LINE Group state
  const [groups, setGroups] = useState<LineGroupMapping[]>([])
  const [loadingGroups, setLoadingGroups] = useState(false)
  const [generatedToken, setGeneratedToken] = useState<string | null>(null)
  const [previousGeneratedToken, setPreviousGeneratedToken] = useState<string | null>(null)
  const [tokenExpiry, setTokenExpiry] = useState<string | null>(null)
  const [generatingToken, setGeneratingToken] = useState(false)
  const [tokenRegenerationStatus, setTokenRegenerationStatus] = useState<TokenRegenerationStatus>("idle")
  const [lastTokenGeneratedAt, setLastTokenGeneratedAt] = useState<string | null>(null)
  const [copiedToken, setCopiedToken] = useState(false)
  const [deletingGroup, setDeletingGroup] = useState<string | null>(null)

  // Branch selection for group token
  const [branches, setBranches] = useState<Branch[]>([])
  const [loadingBranches, setLoadingBranches] = useState(false)
  const [selectedBranchId, setSelectedBranchId] = useState<string>("")
  const [tokenBranchId, setTokenBranchId] = useState<string | null>(null)
  const step4PreviewRef = useRef<HTMLDivElement | null>(null)

  // LINE Chat default branch state
  const [messagingAccounts, setMessagingAccounts] = useState<MessagingAccount[]>([])
  const [settingDefaultBranch, setSettingDefaultBranch] = useState<string | null>(null)

  const loadChannels = useCallback(async () => {
    setLoading(true)
    try {
      const data = await lineChannelsAPI.getAll()
      setChannels(data)
    } catch (err: any) {
      console.error("Failed to load LINE channels:", err)
    } finally {
      setLoading(false)
    }
  }, [])

  const loadLinkedAccounts = useCallback(async () => {
    try {
      await linkedAccountsAPI.getAll()
    } catch (err: any) {
      console.error("Failed to load linked accounts:", err)
    }
  }, [])

  const loadGroups = useCallback(async () => {
    setLoadingGroups(true)
    try {
      const data = await lineGroupsAPI.getAll()
      setGroups(data)
    } catch (err: any) {
      console.error("Failed to load LINE groups:", err)
    } finally {
      setLoadingGroups(false)
    }
  }, [])

  const loadBranches = useCallback(async () => {
    setLoadingBranches(true)
    try {
      const data = await branchesAPI.getAll()
      setBranches(data)
    } catch (err: any) {
      console.error("Failed to load branches:", err)
    } finally {
      setLoadingBranches(false)
    }
  }, [])

  const loadMessagingAccounts = useCallback(async () => {
    try {
      const data = await messagingAccountsAPI.getAll()
      // Show only LINE 1:1 chat accounts (platform = 'line' with channel_source = 'shared')
      setMessagingAccounts(data.filter(a => a.is_active))
    } catch (err: any) {
      console.error("Failed to load messaging accounts:", err)
    }
  }, [])

  useEffect(() => { loadChannels() }, [loadChannels])
  useEffect(() => {
    if (activeTab === "line-oa") {
      loadBranches()
    }
    if (activeTab === "line-chat") {
      loadLinkedAccounts()
      loadMessagingAccounts()
      loadBranches()
    }
    if (activeTab === "line-group") {
      loadGroups()
      loadBranches()
    }
  }, [activeTab, loadLinkedAccounts, loadGroups, loadBranches, loadMessagingAccounts])
  useEffect(() => { setActiveTab(selectedType) }, [selectedType])

  const isLineOAConnected = channels.some((c) => c.is_active)

  const handleLineChatDisconnect = async (platform: string, platformUserId: string, displayName?: string) => {
    const label = displayName || platformUserId
    if (!confirm(`ยืนยันการยกเลิกการเชื่อมต่อ LINE THAISLIPCHECK\n(${label})?`)) return
    setDisconnectingChat(platformUserId)
    try {
      await linkedAccountsAPI.unlink(platform, platformUserId)
      toast.success("ยกเลิกการเชื่อมต่อเรียบร้อยแล้ว")
      await Promise.allSettled([loadLinkedAccounts(), loadMessagingAccounts()])
      onChannelsChange?.()
    } catch {
      toast.error("ไม่สามารถยกเลิกการเชื่อมต่อได้ กรุณาลองใหม่")
    } finally {
      setDisconnectingChat(null)
    }
  }

  // Tab configuration
  const tabs = [
    {
      id: "line-oa" as ConnectionType,
      label: "LINE OA",
      icon: "/icons/icon-line-oa.svg",
      connected: isLineOAConnected,
    },
    {
      id: "line-chat" as ConnectionType,
      label: "LINE แชท",
      icon: "/icons/icon-line.svg",
      connected: isLineChatConnected,
    },
    {
      id: "line-group" as ConnectionType,
      label: "LINE กลุ่ม",
      icon: "/icons/icon-line-chat.svg",
      connected: groups.length > 0,
    },
  ]

  const handleConnect = async () => {
    if (!channelId.trim()) { toast.error("กรุณากรอก Channel ID"); return }
    if (!channelSecret.trim()) { toast.error("กรุณากรอก Channel Secret"); return }
    if (!channelAccessToken.trim()) { toast.error("กรุณากรอก Channel Access Token"); return }
    const businessId = getBusinessId()
    if (!businessId) { toast.error("ไม่พบข้อมูลร้านค้า กรุณาเข้าสู่ระบบใหม่"); return }
    setSubmitting(true)
    try {
      await lineChannelsAPI.register({
        business_id: businessId,
        channel_id: channelId.trim(),
        channel_secret: channelSecret.trim(),
        channel_access_token: channelAccessToken.trim(),
        channel_name: channelName.trim() || undefined,
        branch_id: selectedOABranchId || null,
      })
      toast.success("เชื่อมต่อ LINE OA สำเร็จ!")
      setChannelId(""); setChannelSecret(""); setChannelAccessToken(""); setChannelName(""); setSelectedOABranchId("")
      await loadChannels()
      onChannelsChange?.()
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "เชื่อมต่อไม่สำเร็จ กรุณาตรวจสอบข้อมูลอีกครั้ง"
      )
    } finally {
      setSubmitting(false)
    }
  }

  const handleDisconnect = async (id: string) => {
    if (!confirm("ยืนยันการยกเลิกการเชื่อมต่อ LINE OA?")) return
    setDisconnecting(id)
    try {
      await lineChannelsAPI.delete(id)
      toast.success("ยกเลิกการเชื่อมต่อเรียบร้อยแล้ว")
      await loadChannels()
      onChannelsChange?.()
    } catch {
      toast.error("ไม่สามารถยกเลิกการเชื่อมต่อได้ กรุณาลองใหม่")
    } finally {
      setDisconnecting(null)
    }
  }

  const handleCopyWebhook = (url: string) => {
    navigator.clipboard.writeText(url)
    setCopiedWebhook(true)
    toast.success("คัดลอก Webhook URL แล้ว")
    setTimeout(() => setCopiedWebhook(false), 2000)
  }

  const previewCommand = generatedToken ? `/link ${generatedToken}` : "/link GLK_xxxxxxxxxxxx"

  const handleCopyPreviewCommand = () => {
    if (!generatedToken) return
    navigator.clipboard.writeText(previewCommand)
    toast.success("คัดลอกคำสั่งแล้ว")
  }

  return (
    <div className="p-6">
      <Toaster position="top-right" />

      {/* Tabs */}
      <div className="border border-gray-300 rounded-lg p-2 mb-6">
        <div className="grid grid-cols-3 gap-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex flex-col items-center gap-2 p-3 rounded-lg transition-colors ${
                activeTab === tab.id ? "bg-gray-50" : ""
              }`}
            >
              <Image
                src={tab.icon}
                alt={tab.label}
                width={48}
                height={48}
                className="w-12 h-12"
                style={{
                  filter: "invert(64%) sepia(88%) saturate(364%) hue-rotate(75deg) brightness(95%) contrast(85%)",
                }}
              />
              <div className="flex gap-x-1 items-center">
                <div className={`w-3 h-3 rounded-full ${tab.connected ? "bg-brand-primary" : "bg-brand-red"}`} />
                <span className="text-xs font-medium text-gray-700">{tab.label}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Title + real-time status */}
      <div className="mb-6 flex items-center">
        <h3 className="grow text-xl font-semibold text-gray-900">
          {activeTab === "line-oa" ? "LINE OA" : activeTab === "line-chat" ? "LINE แชท" : "LINE กลุ่มแชท"}
        </h3>
        {loading && activeTab === "line-oa" ? (
          <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
        ) : (
          <span
            className={`inline-flex items-center gap-1 ${
              (activeTab === "line-oa" && isLineOAConnected) ||
              (activeTab === "line-chat" && isLineChatConnected) ||
              activeTab === "line-group"
                ? "text-brand-primary"
                : "text-brand-red"
            }`}
          >
            <span
              className={`w-2 h-2 rounded-full ${
                (activeTab === "line-oa" && isLineOAConnected) ||
                (activeTab === "line-chat" && isLineChatConnected) ||
                activeTab === "line-group"
                  ? "bg-brand-primary"
                  : "bg-brand-red"
              }`}
            />
            <span className="text-sm font-medium">
              {activeTab === "line-oa"
                ? (isLineOAConnected ? "เชื่อมต่อแล้ว" : "ยังไม่ได้เชื่อมต่อ")
                : activeTab === "line-chat"
                ? (isLineChatConnected ? "เชื่อมต่อแล้ว" : "ยังไม่ได้เชื่อมต่อ")
                : "เชื่อมต่อแล้ว"}
            </span>
          </span>
        )}
      </div>

      {/* ═══ LINE OA Tab ══════════════════════════════════════════════════════ */}
      {activeTab === "line-oa" && (
        <>
          {/* Connected channels list */}
          {channels.length > 0 && (
            <div className="mb-6 space-y-3">
              {channels.map((ch) => (
                <div key={ch.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <CheckCircle2 className="w-4 h-4 text-brand-primary flex-shrink-0" />
                        <p className="text-sm font-semibold text-gray-900 truncate">
                          {ch.channel_name || `Channel ${ch.channel_id}`}
                        </p>
                      </div>
                      <p className="text-xs text-gray-500 mb-1">Channel ID: {ch.channel_id}</p>
                      {ch.branch_name ? (
                        <div className="flex items-center gap-1 mb-1">
                          <GitBranch className="w-3 h-3 text-brand-primary" />
                          <span className="text-xs text-brand-primary font-medium">{ch.branch_name}</span>
                        </div>
                      ) : (
                        <p className="text-xs text-gray-400 mb-1">สาขา: อัตโนมัติ (MAIN Branch)</p>
                      )}
                      {ch.webhook_url && (
                        <div className="flex items-center gap-2 mt-2">
                          <div className="flex-1 min-w-0 bg-white border border-gray-200 rounded px-2 py-1">
                            <p className="text-xs text-gray-600 truncate font-mono">{ch.webhook_url}</p>
                          </div>
                          <button
                            onClick={() => handleCopyWebhook(ch.webhook_url!)}
                            className="flex-shrink-0 p-1 hover:bg-gray-200 rounded transition-colors"
                            title="คัดลอก Webhook URL"
                          >
                            {copiedWebhook
                              ? <CheckCircle2 className="w-4 h-4 text-green-600" />
                              : <Copy className="w-4 h-4 text-gray-500" />}
                          </button>
                        </div>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-brand-red border-brand-red hover:bg-brand-red hover:text-white flex-shrink-0"
                      disabled={disconnecting === ch.id}
                      onClick={() => handleDisconnect(ch.id)}
                    >
                      {disconnecting === ch.id
                        ? <Loader2 className="w-3 h-3 animate-spin" />
                        : <><Trash2 className="w-3 h-3 mr-1" />ยกเลิก</>}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Step-by-step setup guide */}
          <div className="mb-6 space-y-2">
            <p className="text-sm font-semibold text-gray-700 mb-3">
              {channels.length > 0 ? "เพิ่ม LINE OA อีกช่องทาง" : "ขั้นตอนการเชื่อมต่อ LINE OA"}
            </p>

            {/* Step 1 */}
            <div className="flex gap-3 items-start p-3 rounded-lg bg-gray-50 border border-gray-200">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-brand-primary text-white text-xs font-bold flex items-center justify-center mt-0.5">1</div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-gray-900">เปิด LINE Developers Console</p>
                <p className="text-xs text-gray-500 mt-0.5">เข้าไปที่ <span className="font-medium text-brand-primary">developers.line.biz</span> → เลือก Channel ที่ต้องการ → แท็บ <span className="font-medium">Messaging API</span></p>
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-2 text-xs h-7 text-brand-primary border-brand-primary/40 hover:bg-brand-primary/5"
                  onClick={() => window.open("https://developers.line.biz/console/", "_blank")}
                >
                  <ExternalLink className="w-3 h-3 mr-1" />เปิด LINE Developers
                </Button>
              </div>
            </div>

            {/* Step 2 */}
            <div className="flex gap-3 items-start p-3 rounded-lg bg-gray-50 border border-gray-200">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-brand-primary text-white text-xs font-bold flex items-center justify-center mt-0.5">2</div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-gray-900">คัดลอก Channel Credentials</p>
                <ul className="mt-1 space-y-1 text-xs text-gray-500">
                  <li>• <span className="font-medium">Channel ID</span> — อยู่ที่แท็บ Basic settings</li>
                  <li>• <span className="font-medium">Channel secret</span> — อยู่ที่แท็บ Basic settings</li>
                  <li>• <span className="font-medium">Channel access token</span> — อยู่ที่แท็บ Messaging API (กด Issue ถ้ายังไม่มี)</li>
                </ul>
              </div>
            </div>

            {/* Step 3 */}
            <div className="flex gap-3 items-start p-3 rounded-lg bg-gray-50 border border-gray-200">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-brand-primary text-white text-xs font-bold flex items-center justify-center mt-0.5">3</div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-gray-900">กรอกข้อมูลและเชื่อมต่อ</p>
                <p className="text-xs text-gray-500 mt-0.5">กรอกข้อมูลในฟอร์มด้านล่าง แล้วกด "เชื่อมต่อ"</p>
              </div>
            </div>

            {/* Step 4 */}
            <div className="flex gap-3 items-start p-3 rounded-lg bg-gray-50 border border-gray-200">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-brand-primary text-white text-xs font-bold flex items-center justify-center mt-0.5">4</div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-gray-900">ตั้ง Webhook URL ใน LINE</p>
                <p className="text-xs text-gray-500 mt-0.5">หลังเชื่อมต่อสำเร็จ คัดลอก Webhook URL แล้วนำไปวางใน LINE Developers Console → แท็บ Messaging API → Webhook URL</p>
                <ul className="mt-1 space-y-1 text-xs text-gray-500">
                  <li>• เปิด <span className="font-medium">Use webhook</span> ให้เป็น ON</li>
                  <li>• กด <span className="font-medium">Verify</span> เพื่อทดสอบ</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Registration form */}
          <div className="space-y-4 mb-6">
            <div>
              <label className="text-sm font-medium text-gray-900 mb-2 block">
                ชื่อช่องทาง (Channel Name)
              </label>
              <Input
                placeholder="เช่น LINE OA ร้านหลัก"
                value={channelName}
                onChange={(e) => setChannelName(e.target.value)}
                className="w-full"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-900 mb-2 block">
                ชาแนล ID (Channel ID)<span className="text-red-500">*</span>
              </label>
              <Input
                placeholder="กรอก Channel ID"
                value={channelId}
                onChange={(e) => setChannelId(e.target.value)}
                className="w-full"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-900 mb-2 block">
                ความลับชาแนล (Channel Secret)<span className="text-red-500">*</span>
              </label>
              <Input
                placeholder="กรอก Channel Secret"
                type="password"
                value={channelSecret}
                onChange={(e) => setChannelSecret(e.target.value)}
                className="w-full"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-900 mb-2 block">
                Channel Access Token<span className="text-red-500">*</span>
              </label>
              <Input
                placeholder="กรอก Channel Access Token"
                type="password"
                value={channelAccessToken}
                onChange={(e) => setChannelAccessToken(e.target.value)}
                className="w-full"
              />
            </div>

            {/* Branch selector */}
            <div>
              <label className="text-sm font-medium text-gray-900 mb-1 flex items-center gap-1.5">
                <GitBranch className="w-4 h-4 text-brand-primary" />
                สาขาที่รับสลิป (LINE OA 1:1)
                <span className="text-xs text-gray-400 font-normal">(ไม่บังคับ)</span>
              </label>
              <p className="text-xs text-gray-500 mb-2">
                ผู้ใช้ที่แชท 1:1 กับ LINE OA นี้จะถูกส่งสลิปเข้าสาขาที่เลือก<br/>
                หากไม่เลือก ระบบจะ auto-resolve ไปยัง MAIN Branch อัตโนมัติ
              </p>
              {loadingBranches ? (
                <div className="flex items-center gap-2 text-xs text-gray-400 py-2">
                  <Loader2 className="w-3 h-3 animate-spin" /> กำลังโหลดสาขา...
                </div>
              ) : (
                <select
                  value={selectedOABranchId}
                  onChange={(e) => setSelectedOABranchId(e.target.value)}
                  className="w-full text-sm border border-gray-200 rounded-md px-3 py-2 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-brand-primary/50"
                >
                  <option value="">— อัตโนมัติ (MAIN Branch) —</option>
                  {branches.filter((b) => b.is_active).map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.branch_name}{b.is_main_branch ? " (หลัก)" : ""}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div className="text-center">
              <span className="text-brand-primary text-sm">
                สถานะจะแสดงว่า "เชื่อมต่อแล้ว" เมื่อเชื่อมต่อสำเร็จ
              </span>
            </div>

            <div className="flex justify-end">
              <Button
                className="bg-brand-primary hover:bg-brand-primary/90 text-white px-8"
                onClick={handleConnect}
                disabled={submitting}
              >
                {submitting
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />กำลังเชื่อมต่อ...</>
                  : "เชื่อมต่อ"}
              </Button>
            </div>
          </div>
        </>
      )}

      {/* ═══ LINE Chat Tab ════════════════════════════════════════════════════ */}
      {activeTab === "line-chat" && (
        <div className="space-y-6 mb-6">
          {/* Connected: show account info + disconnect button */}
          {isLineChatConnected && messagingAccounts.filter((a) => (a.platform === "line" || a.platform === "line-oa") && a.is_active).length > 0 && (
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 space-y-3">
              {messagingAccounts
                .filter((a) => (a.platform === "line" || a.platform === "line-oa") && a.is_active)
                .map((a) => (
                  <div key={a.platform_user_id} className="flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <CheckCircle2 className="w-4 h-4 text-brand-primary flex-shrink-0" />
                        <p className="text-sm font-semibold text-gray-900">
                          {a.platform_username || "LINE THAISLIPCHECK"}
                        </p>
                      </div>
                      <p className="text-xs text-gray-400 font-mono truncate">{a.platform_user_id}</p>
                      <p className="text-xs text-gray-500">เชื่อมต่อเมื่อ {new Date(a.linked_at).toLocaleDateString("th-TH")}</p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-brand-red border-brand-red hover:bg-brand-red hover:text-white flex-shrink-0"
                      disabled={disconnectingChat === a.platform_user_id}
                      onClick={() => handleLineChatDisconnect(a.platform, a.platform_user_id, a.platform_username ?? undefined)}
                    >
                      {disconnectingChat === a.platform_user_id
                        ? <Loader2 className="w-3 h-3 animate-spin" />
                        : <><Trash2 className="w-3 h-3 mr-1" />ยกเลิก</>}
                    </Button>
                  </div>
                ))}
            </div>
          )}

          {/* Default branch setting for 1:1 LINE chat */}
          {isLineChatConnected && messagingAccounts.length > 0 && (
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <div className="flex items-center gap-2 mb-3">
                <GitBranch className="w-4 h-4 text-brand-primary" />
                <p className="text-sm font-semibold text-gray-900">ตั้งค่าสาขารับสลิป (LINE แชท 1:1)</p>
              </div>
              <div className="space-y-3">
                {messagingAccounts
                  .filter((a) => a.platform === "line" || a.platform === "line-oa")
                  .map((a) => (
                    <div key={a.id} className="flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-gray-700 mb-0.5 truncate">
                          {a.platform_username || "LINE THAISLIPCHECK"}
                        </p>
                        <p className="text-xs text-gray-400 font-mono truncate mb-1">{a.platform_user_id}</p>
                        <select
                          className="w-full text-xs border border-gray-200 rounded px-2 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-brand-primary disabled:opacity-50"
                          value={a.default_branch_id || ""}
                          disabled={settingDefaultBranch === a.id || loadingBranches}
                          onChange={async (e) => {
                            const branchId = e.target.value || null
                            setSettingDefaultBranch(a.id)
                            try {
                              await messagingAccountsAPI.setDefaultBranch(a.id, branchId)
                              setMessagingAccounts((prev) =>
                                prev.map((acc) =>
                                  acc.id === a.id ? { ...acc, default_branch_id: branchId } : acc
                                )
                              )
                              toast.success("บันทึกสาขาเริ่มต้นแล้ว")
                            } catch {
                              toast.error("ไม่สามารถบันทึกสาขาได้")
                            } finally {
                              setSettingDefaultBranch(null)
                            }
                          }}
                        >
                          <option value="">— อัตโนมัติ (MAIN Branch) —</option>
                          {branches.filter((b) => b.is_active).map((b) => (
                            <option key={b.id} value={b.id}>
                              {b.branch_name}{b.is_main_branch ? " (หลัก)" : ""}
                            </option>
                          ))}
                        </select>
                      </div>
                      {settingDefaultBranch === a.id && (
                        <Loader2 className="w-4 h-4 animate-spin text-gray-400 flex-shrink-0" />
                      )}
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Instructions for adding friend */}
          <div className="space-y-2">
            {/* Step 1 */}
            <div className="flex gap-3 items-start p-3 rounded-lg bg-gray-50 border border-gray-200">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-brand-primary text-white text-xs font-bold flex items-center justify-center mt-0.5">1</div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-gray-900">เพิ่ม LINE THAISLIPCHECK เป็นเพื่อน</p>
                <p className="text-xs text-gray-500 mt-0.5">สแกน QR Code หรือกดปุ่มด้านล่าง แล้วกด "เพิ่มเพื่อน" ใน LINE</p>
                <div className="mt-2 flex items-center gap-3">
                  <Image
                    src="/images/qr-connect.jpg"
                    alt="QR Code"
                    width={64}
                    height={64}
                    className="w-16 h-16 rounded border border-gray-200"
                  />
                  <Button
                    size="sm"
                    className="bg-[#06C755] hover:bg-[#05b34c] text-white rounded-full px-4"
                    onClick={() => window.open("https://line.me/R/ti/p/@thaislipcheck", "_blank")}
                  >
                    เพิ่มเพื่อน Thaislipcheck
                  </Button>
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="flex gap-3 items-start p-3 rounded-lg bg-gray-50 border border-gray-200">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-brand-primary text-white text-xs font-bold flex items-center justify-center mt-0.5">2</div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-gray-900">เปิดแชท 1:1 กับ THAISLIPCHECK แล้วพิมพ์คำสั่ง</p>
                <p className="text-xs text-gray-500 mt-1">พิมพ์คำสั่ง <span className="font-mono font-semibold text-gray-700">/link</span> ตามด้วยข้อมูลยืนยันตัวตนของคุณ:</p>
                <div className="mt-2 space-y-1.5">
                  <div className="bg-white border border-gray-200 rounded px-3 py-1.5 font-mono text-xs text-gray-700">/link <span className="text-brand-primary">scb_api_xxxxx</span> <span className="text-gray-400 font-sans">(API Key)</span></div>
                  <div className="bg-white border border-gray-200 rounded px-3 py-1.5 font-mono text-xs text-gray-700">/link <span className="text-brand-primary">email@example.com</span> <span className="text-gray-400 font-sans">(อีเมล)</span></div>
                  <div className="bg-white border border-gray-200 rounded px-3 py-1.5 font-mono text-xs text-gray-700">/link <span className="text-brand-primary">0891234567</span> <span className="text-gray-400 font-sans">(เบอร์โทรศัพท์)</span></div>
                </div>
              </div>
            </div>

            {/* Step 3 */}
            <div className="flex gap-3 items-start p-3 rounded-lg bg-gray-50 border border-gray-200">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-brand-primary text-white text-xs font-bold flex items-center justify-center mt-0.5">3</div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-gray-900">ยืนยัน OTP</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  ระบบจะส่งรหัส OTP ไปยัง SMS หรืออีเมลที่ลงทะเบียนไว้<br/>
                  พิมพ์รหัส OTP ที่ได้รับกลับไปในแชทเพื่อยืนยัน — บัญชีจะเชื่อมต่อสำเร็จทันที
                </p>
              </div>
            </div>
          </div>

          <div className="text-center">
            <span className="text-brand-primary text-sm">ระบบจะแสดงข้อความตอบกลับ เมื่อการเชื่อมต่อสำเร็จ</span>
          </div>
        </div>
      )}

      {/* ═══ LINE Group Tab ═══════════════════════════════════════════════════ */}
      {activeTab === "line-group" && (
        <div className="space-y-5 mb-6">

          {/* ── ขั้นตอนการเชื่อมกลุ่ม LINE ── */}
          <div className="space-y-2">
            {/* Step 1 */}
            <div className="flex gap-3 items-start p-3 rounded-lg bg-gray-50 border border-gray-200">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-brand-primary text-white text-xs font-bold flex items-center justify-center mt-0.5">1</div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-gray-900">เพิ่มเพื่อน LINE Thaislipcheck</p>
                <p className="text-xs text-gray-500 mt-0.5">กดปุ่มด้านล่างเพื่อเพิ่มบอทเป็นเพื่อนใน LINE ก่อน</p>
                <div className="mt-2 flex items-center gap-3">
                  <Image
                    src="/images/qr-connect.jpg"
                    alt="QR Code"
                    width={64}
                    height={64}
                    className="w-16 h-16 rounded border border-gray-200"
                  />
                  <Button
                    size="sm"
                    className="bg-[#06C755] hover:bg-[#05b34c] text-white rounded-full px-4"
                    onClick={() => window.open("https://line.me/R/ti/p/@thaislipcheck", "_blank")}
                  >
                    เพิ่มเพื่อน Thaislipcheck
                  </Button>
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="flex gap-3 items-start p-3 rounded-lg bg-gray-50 border border-gray-200">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-brand-primary text-white text-xs font-bold flex items-center justify-center mt-0.5">2</div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-gray-900">เพิ่ม Thaislipcheck เข้ากลุ่ม LINE</p>
                <ul className="mt-1 space-y-1 text-xs text-gray-500">
                  <li>• เปิดกลุ่ม LINE ที่ต้องการ หรือสร้างกลุ่มใหม่</li>
                  <li>• กด <span className="font-medium">เพิ่มสมาชิก</span> → ค้นหา <span className="font-medium text-[#06C755]">Thaislipcheck</span> → เพิ่มเข้ากลุ่ม</li>
                </ul>
              </div>
            </div>

            {/* Step 3 */}
            <div className={`flex gap-3 items-start p-3 rounded-lg border transition-colors ${generatedToken ? "bg-brand-primary/5 border-brand-primary/30" : "bg-gray-50 border-gray-200"}`}>
              <div className={`flex-shrink-0 w-6 h-6 rounded-full text-white text-xs font-bold flex items-center justify-center mt-0.5 ${generatedToken ? "bg-brand-primary" : "bg-brand-primary"}`}>3</div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">สร้าง Link Token</p>
                    <p className="text-xs text-gray-500 mt-0.5">Token มีอายุ 24 ชั่วโมง ใช้ได้ 1 ครั้ง</p>
                  </div>
                  <Button
                    size="sm"
                    className="bg-brand-primary hover:bg-brand-primary/90 text-white"
                    onClick={async () => {
                      const priorToken = generatedToken
                      setGeneratingToken(true)
                      setCopiedToken(false)
                      try {
                        const res = await lineGroupsAPI.createToken(undefined, selectedBranchId || null)
                        setPreviousGeneratedToken(priorToken)
                        setGeneratedToken(res.token)
                        setTokenExpiry(res.expires_at)
                        setTokenBranchId(res.branch_id)
                        setLastTokenGeneratedAt(new Date().toISOString())

                        if (priorToken && priorToken === res.token) {
                          setTokenRegenerationStatus("unchanged")
                          toast("ระบบตอบกลับ Token เดิม กรุณาตรวจสอบฝั่งเซิร์ฟเวอร์")
                        } else {
                          setTokenRegenerationStatus("changed")
                          toast.success(priorToken ? "สร้าง Token ใหม่สำเร็จ! อัปเดตคำสั่งใน Step 4 แล้ว" : "สร้าง Token สำเร็จ!")
                        }

                        requestAnimationFrame(() => {
                          step4PreviewRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" })
                        })
                      } catch (err: any) {
                        setTokenRegenerationStatus("error")
                        toast.error(err?.response?.data?.message || err?.message || "ไม่สามารถสร้าง Token ได้")
                      } finally {
                        setGeneratingToken(false)
                      }
                    }}
                    disabled={generatingToken}
                  >
                    {generatingToken
                      ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />กำลังสร้าง...</>
                      : <><Link2 className="w-3 h-3 mr-1" />{generatedToken ? "สร้างใหม่" : "สร้าง Token"}</>}
                  </Button>
                </div>

                {/* Branch selector */}
                <div className="mt-3">
                  <label className="text-xs font-medium text-gray-600 flex items-center gap-1 mb-1">
                    <GitBranch className="w-3 h-3" />
                    สาขาที่รับสลิป <span className="text-gray-400 font-normal">(ไม่บังคับ)</span>
                  </label>
                  {loadingBranches ? (
                    <div className="flex items-center gap-1 text-xs text-gray-400 py-1">
                      <Loader2 className="w-3 h-3 animate-spin" /> กำลังโหลดสาขา...
                    </div>
                  ) : (
                    <select
                      value={selectedBranchId}
                      onChange={(e) => {
                        setSelectedBranchId(e.target.value)
                        setGeneratedToken(null) // reset token when branch changes
                        setPreviousGeneratedToken(null)
                        setTokenExpiry(null)
                        setTokenBranchId(null)
                        setCopiedToken(false)
                        setTokenRegenerationStatus("idle")
                        setLastTokenGeneratedAt(null)
                      }}
                      className="w-full text-xs border border-gray-200 rounded px-2 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-brand-primary"
                    >
                      <option value="">— อัตโนมัติ (MAIN Branch) —</option>
                      {branches.filter(b => b.is_active).map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.branch_name}{b.is_main_branch ? " (หลัก)" : ""}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                {/* Token display */}
                {generatedToken && (
                  <div className="mt-3 bg-white border border-brand-primary/40 rounded-lg p-2.5">
                    <p className="text-xs text-gray-400 mb-1">
                      Token · หมดอายุ {tokenExpiry ? new Date(tokenExpiry).toLocaleString("th-TH", { dateStyle: "short", timeStyle: "short" }) : ""}
                      {lastTokenGeneratedAt && (
                        <span className="ml-2 text-gray-500">
                          · อัปเดตล่าสุด {new Date(lastTokenGeneratedAt).toLocaleTimeString("th-TH", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                        </span>
                      )}
                      {tokenBranchId && branches.find(b => b.id === tokenBranchId) && (
                        <span className="ml-2 text-brand-primary">
                          · สาขา: {branches.find(b => b.id === tokenBranchId)?.branch_name}
                        </span>
                      )}
                    </p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-xs font-mono text-brand-primary font-semibold break-all">
                        {generatedToken}
                      </code>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(generatedToken)
                          setCopiedToken(true)
                          toast.success("คัดลอก Token แล้ว")
                          setTimeout(() => setCopiedToken(false), 2000)
                        }}
                        className="flex-shrink-0 p-1.5 hover:bg-gray-100 rounded transition-colors"
                        title="คัดลอก Token"
                      >
                        {copiedToken
                          ? <CheckCircle2 className="w-4 h-4 text-green-600" />
                          : <Copy className="w-4 h-4 text-gray-500" />}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Step 4 */}
            <div
              ref={step4PreviewRef}
              className={`flex gap-3 items-start p-3 rounded-lg border transition-colors ${
                generatedToken
                  ? tokenRegenerationStatus === "unchanged"
                    ? "bg-amber-50 border-amber-200"
                    : "bg-blue-50 border-blue-200"
                  : "bg-gray-50 border-gray-200 opacity-50"
              }`}
            >
              <div className={`flex-shrink-0 w-6 h-6 rounded-full text-white text-xs font-bold flex items-center justify-center mt-0.5 ${generatedToken ? "bg-blue-500" : "bg-gray-300"}`}>4</div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-gray-900">พิมพ์คำสั่งในกลุ่ม LINE</p>
                  {generatedToken && tokenRegenerationStatus === "changed" && (
                    <span className="text-[10px] font-semibold text-blue-700 bg-blue-100 border border-blue-200 px-1.5 py-0.5 rounded">อัปเดตแล้ว</span>
                  )}
                  {generatedToken && tokenRegenerationStatus === "unchanged" && (
                    <span className="text-[10px] font-semibold text-amber-700 bg-amber-100 border border-amber-200 px-1.5 py-0.5 rounded">Token เดิม</span>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-0.5">เปิดกลุ่มที่เพิ่มบอทไว้ แล้วส่งข้อความนี้:</p>
                <div className="mt-2 flex items-center gap-2">
                  <code
                    className={`flex-1 rounded px-2 py-1.5 text-xs font-mono break-all ${generatedToken ? "bg-white border border-blue-200 text-blue-800 cursor-pointer hover:bg-blue-50" : "bg-gray-100 text-gray-400 border border-gray-200"}`}
                    onClick={handleCopyPreviewCommand}
                    title={generatedToken ? "คลิกเพื่อคัดลอก" : "สร้าง Token ก่อน"}
                  >
                    {previewCommand}
                  </code>
                  <button
                    disabled={!generatedToken}
                    onClick={handleCopyPreviewCommand}
                    className="flex-shrink-0 p-1 rounded transition-colors disabled:opacity-30 hover:bg-blue-100"
                  >
                    <Copy className={`w-3.5 h-3.5 ${generatedToken ? "text-blue-600" : "text-gray-400"}`} />
                  </button>
                </div>
                {generatedToken && tokenRegenerationStatus === "unchanged" && previousGeneratedToken === generatedToken && (
                  <p className="text-xs mt-2 text-amber-700">
                    ระบบสร้างใหม่แล้วแต่ได้ Token เดิมจากเซิร์ฟเวอร์ กรุณาตรวจสอบการตั้งค่าฝั่ง API
                  </p>
                )}
                <p className={`text-xs mt-2 ${generatedToken ? "text-blue-600" : "text-gray-400"}`}>
                  บอทจะตอบกลับว่า ✅ เชื่อมต่อสำเร็จ — จากนั้นส่งสลิปในกลุ่มได้เลย
                </p>
              </div>
            </div>
          </div>

          {/* ── รายการกลุ่มที่เชื่อมแล้ว ── */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-gray-900">กลุ่มที่เชื่อมต่อแล้ว</p>
              <button
                onClick={loadGroups}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
                title="รีเฟรช"
              >
                <RefreshCw className={`w-3.5 h-3.5 text-gray-400 ${loadingGroups ? "animate-spin" : ""}`} />
              </button>
            </div>

            {loadingGroups ? (
              <div className="flex justify-center py-6">
                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
              </div>
            ) : groups.length === 0 ? (
              <div className="border border-dashed border-gray-200 rounded-lg p-6 text-center">
                <p className="text-sm text-gray-400">ยังไม่มีกลุ่มที่เชื่อมต่อ</p>
                <p className="text-xs text-gray-300 mt-1">สร้าง Token แล้วนำไปพิมพ์ในกลุ่ม LINE</p>
              </div>
            ) : (
              <div className="space-y-2">
                {groups.map((g) => (
                  <div key={g.id} className="border border-gray-200 rounded-lg p-3 bg-gray-50 flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-3.5 h-3.5 text-brand-primary flex-shrink-0" />
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {g.business_name || "กลุ่ม LINE"}
                        </p>
                        <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
                          {g.group_type === "room" ? "ห้องแชท" : "กลุ่ม"}
                        </span>
                      </div>
                      {g.branch_name && (
                        <div className="flex items-center gap-1 pl-5 mt-0.5">
                          <GitBranch className="w-3 h-3 text-brand-primary" />
                          <span className="text-xs text-brand-primary font-medium">{g.branch_name}</span>
                        </div>
                      )}
                      {!g.branch_name && (
                        <p className="text-xs text-gray-400 mt-0.5 pl-5">สาขา: อัตโนมัติ (MAIN Branch)</p>
                      )}
                      <p className="text-xs text-gray-400 mt-0.5 pl-5">
                        เชื่อมเมื่อ {new Date(g.created_at).toLocaleDateString("th-TH")}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-brand-red border-brand-red hover:bg-brand-red hover:text-white flex-shrink-0"
                      disabled={deletingGroup === g.id}
                      onClick={async () => {
                        if (!confirm("ยืนยันการยกเลิกการเชื่อมต่อกลุ่มนี้?")) return
                        setDeletingGroup(g.id)
                        try {
                          await lineGroupsAPI.delete(g.id)
                          toast.success("ยกเลิกการเชื่อมต่อเรียบร้อยแล้ว")
                          await loadGroups()
                          onChannelsChange?.()
                        } catch {
                          toast.error("ไม่สามารถยกเลิกการเชื่อมต่อได้ กรุณาลองใหม่")
                        } finally {
                          setDeletingGroup(null)
                        }
                      }}
                    >
                      {deletingGroup === g.id
                        ? <Loader2 className="w-3 h-3 animate-spin" />
                        : <><Trash2 className="w-3 h-3 mr-1" />ยกเลิก</>}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
